/**
 * §P.1 · Contactos de emergencia.
 *
 * Dos cosas que este componente resuelve y que se pueden hacer mal sin notarlo:
 *
 * **El orden de la lista ES la prioridad.** «El primero es a quien se llama», y
 * quien la ordena en pantalla está diciendo a quién llamar primero. Así que el
 * orden no es una preferencia de visualización: es el dato. Por eso hay flechas
 * para moverlos y por eso el primero lleva un cartel que lo dice.
 *
 * **El `PUT` reemplaza la lista entera.** No hay altas ni bajas sueltas —«con
 * altas y bajas sueltas la pantalla tendría que llevar la cuenta de qué borró para
 * mandarlo aparte, y son tres teléfonos»—. Se edita la lista completa y se manda
 * completa.
 *
 * Y el entrenador **los ve pero no los cambia**: la lectura es suya porque es
 * quien tiene el móvil en la mano en el peor momento; el orden lo decide el club.
 */
import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '@/shared/api/client';
import { aProblema, ErrorDeApi, type Problema } from '@/shared/api/errores';
import { Cargando, Error as PanelError, Vacio } from '@/shared/ui/Estados';
import {
  MAXIMO_DE_CONTACTOS,
  type ContactoDeEmergencia,
} from '@/club/alumnos/salud/tipos';

const vacio = (): ContactoDeEmergencia => ({
  full_name: '',
  phone: '',
  relationship: '',
});

/** Mueve un elemento. Devuelve una lista nueva; si no cabe el movimiento, la misma. */
export function mover<T>(
  lista: readonly T[],
  desde: number,
  hacia: number
): readonly T[] {
  if (hacia < 0 || hacia >= lista.length || desde === hacia) return lista;
  const copia = [...lista];
  const [sacado] = copia.splice(desde, 1);
  copia.splice(hacia, 0, sacado!);
  return copia;
}

/** Vale para mandar: nombre y teléfono. El parentesco es opcional. */
export const estaCompleto = (c: ContactoDeEmergencia): boolean =>
  c.full_name.trim().length >= 2 && c.phone.trim().length >= 5;

export function ContactosDeEmergencia({
  club,
  alumnoId,
  puedeEditar,
}: {
  club: string;
  alumnoId: string;
  puedeEditar: boolean;
}) {
  const cache = useQueryClient();
  const [borrador, setBorrador] = useState<
    readonly ContactoDeEmergencia[] | null
  >(null);
  const [problema, setProblema] = useState<Problema | null>(null);

  const contactos = useQuery({
    queryKey: ['contactos', club, alumnoId],
    queryFn: async () => {
      const r = await api.v1
        .clubs({ club })
        .students({ studentId: alumnoId })
        ['emergency-contacts'].get();
      if (r.error) throw new ErrorDeApi(aProblema(r.error));
      return r.data as unknown as ContactoDeEmergencia[];
    },
  });

  const guardar = useMutation({
    mutationFn: async (lista: readonly ContactoDeEmergencia[]) => {
      const r = await api.v1
        .clubs({ club })
        .students({ studentId: alumnoId })
        ['emergency-contacts'].put({
          // La lista entera y en orden: la posición es la prioridad.
          contacts: lista.filter(estaCompleto).map((c) => ({
            full_name: c.full_name.trim(),
            phone: c.phone.trim(),
            relationship: c.relationship?.trim() || null,
          })),
        });
      if (r.error) throw new ErrorDeApi(aProblema(r.error));
      return r.data;
    },
    onSuccess: () => {
      setProblema(null);
      setBorrador(null);
      void cache.invalidateQueries({ queryKey: ['contactos', club, alumnoId] });
    },
    onError: (e) => setProblema(aProblema(e)),
  });

  if (contactos.isPending)
    return <Cargando filas={2} etiqueta="Cargando los contactos" />;

  if (contactos.isError)
    return (
      <PanelError
        problema={aProblema(contactos.error)}
        reintentar={() => void contactos.refetch()}
      />
    );

  // ── Edición: la lista completa ───────────────────────────────────────────
  if (borrador) {
    const utiles = borrador.filter(estaCompleto).length;

    return (
      <form
        className="formulario"
        onSubmit={(e) => {
          e.preventDefault();
          guardar.mutate(borrador);
        }}
      >
        <p className="tenue">
          El orden manda: <strong>al primero se le llama primero</strong>. Las
          filas sin nombre o sin teléfono no se guardan.
        </p>

        <ul className="contactos-edit">
          {borrador.map((c, i) => (
            <li key={i}>
              <span className="contacto-orden">{i + 1}</span>

              <input
                aria-label={`Nombre del contacto ${i + 1}`}
                placeholder="Nombre y apellidos"
                value={c.full_name}
                onChange={(e) =>
                  setBorrador(
                    borrador.map((x, j) =>
                      j === i ? { ...x, full_name: e.target.value } : x
                    )
                  )
                }
              />

              <input
                aria-label={`Teléfono del contacto ${i + 1}`}
                placeholder="Teléfono"
                value={c.phone}
                onChange={(e) =>
                  setBorrador(
                    borrador.map((x, j) =>
                      j === i ? { ...x, phone: e.target.value } : x
                    )
                  )
                }
              />

              <input
                aria-label={`Parentesco del contacto ${i + 1}`}
                placeholder="Parentesco"
                value={c.relationship ?? ''}
                onChange={(e) =>
                  setBorrador(
                    borrador.map((x, j) =>
                      j === i ? { ...x, relationship: e.target.value } : x
                    )
                  )
                }
              />

              <div className="contacto-botones">
                {/* Flechas y no arrastrar: con teclado, arrastrar no existe. */}
                <button
                  type="button"
                  className="btn-plano"
                  aria-label={`Subir el contacto ${i + 1}`}
                  disabled={i === 0}
                  onClick={() => setBorrador(mover(borrador, i, i - 1))}
                >
                  ↑
                </button>
                <button
                  type="button"
                  className="btn-plano"
                  aria-label={`Bajar el contacto ${i + 1}`}
                  disabled={i === borrador.length - 1}
                  onClick={() => setBorrador(mover(borrador, i, i + 1))}
                >
                  ↓
                </button>
                <button
                  type="button"
                  className="btn-plano"
                  aria-label={`Quitar el contacto ${i + 1}`}
                  onClick={() =>
                    setBorrador(borrador.filter((_, j) => j !== i))
                  }
                >
                  ×
                </button>
              </div>
            </li>
          ))}
        </ul>

        {borrador.length < MAXIMO_DE_CONTACTOS && (
          <button
            type="button"
            className="btn-plano"
            onClick={() => setBorrador([...borrador, vacio()])}
          >
            Añadir otro
          </button>
        )}

        {problema && <PanelError problema={problema} />}

        <div className="acciones">
          <button type="submit" disabled={guardar.isPending}>
            {guardar.isPending
              ? 'Guardando…'
              : utiles === 0
                ? 'Guardar sin ninguno'
                : `Guardar ${utiles} contacto${utiles === 1 ? '' : 's'}`}
          </button>
          <button
            type="button"
            className="btn-plano"
            disabled={guardar.isPending}
            onClick={() => {
              setBorrador(null);
              setProblema(null);
            }}
          >
            Cancelar
          </button>
        </div>
      </form>
    );
  }

  // ── Lectura ──────────────────────────────────────────────────────────────
  if (contactos.data.length === 0) {
    return (
      <Vacio
        titulo="No hay contactos de emergencia"
        explicacion="Son los teléfonos a los que se llama si pasa algo en clase. El entrenador los ve desde su app, así que conviene que estén."
        {...(puedeEditar
          ? {
              accion: (
                <button type="button" onClick={() => setBorrador([vacio()])}>
                  Añadir contactos
                </button>
              ),
            }
          : {})}
      />
    );
  }

  return (
    <>
      <ol className="contactos">
        {contactos.data.map((c, i) => (
          <li key={c.id ?? i}>
            <div>
              <strong>{c.full_name}</strong>
              {i === 0 && (
                // El dato más importante de la lista, dicho y no deducido.
                <span className="chapa">Se llama primero</span>
              )}
              <p className="tenue">
                {c.phone}
                {c.relationship ? ` · ${c.relationship}` : ''}
              </p>
            </div>
          </li>
        ))}
      </ol>

      {puedeEditar && (
        <div className="acciones">
          <button
            type="button"
            className="btn-plano"
            onClick={() => setBorrador(contactos.data.map((c) => ({ ...c })))}
          >
            Editar los contactos
          </button>
        </div>
      )}
    </>
  );
}
